import type React from 'react';
import { Form as ReactFinalForm } from 'react-final-form';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import {
  useIdVerificationCallbacks,
  usePassiveAuthData,
  usePidAnswers,
  useSetPidAnswers,
  useSkipTermsStep,
} from '../../contexts/IdVerificationContext';
import { IdvStep } from '../../../..';
import { getWizardOnSubmit } from '../Content.utils';
import { useEffectOnce } from 'react-use';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../clickstream/events';
import PersonalInfoStepContent from './PersonalInfoStepContent';

interface PersonalInfoStepProps {
  id: string;
  label: string;
}

export default function PersonalInfoStep({ id, label }: PersonalInfoStepProps) {
  const { onRequestStep } = useWizard();
  const skipTermsStep = useSkipTermsStep();
  const { jsc, hdimPayload } = usePassiveAuthData();
  const pidAnswers = usePidAnswers();
  const setPidAnswers = useSetPidAnswers();
  const { trackEvent } = useIdVerificationCallbacks();

  useEffectOnce(() => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('id_verification_provide_details'));
  });

  return (
    <ReactFinalForm
      initialValues={{
        ...pidAnswers,
        jsc,
        hdimPayload,
      }}
      onSubmit={async (formValues) => {
        setPidAnswers((currentPidAnswers) => ({
          ...currentPidAnswers,
          ...formValues,
          /*
            middleName and ssn are optional fields so they must be set to
            an empty string if their values are cleared out otherwise react-final-form
            will default them to 'undefined' resulting in their value never
            being updated. See the link below form more info:
            https://final-form.org/docs/final-form/faq#why-does-final-form-set-my--field-value-to-undefined
          */
          middleName: formValues?.middleName ?? '',
          ssn: formValues?.ssn ?? '',
        }));
      }}
      render={({ form, invalid }) => {
        return (
          <WizardStep
            id={id}
            label={label}
            meta={{
              isNextDisabled: invalid,
            }}
            onRequestNext={() => {
              trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNext('id_verification_provide_details'));
            }}
            onRequestPrev={(abortController: AbortController) => {
              const {
                firstName,
                middleName,
                lastName,
                currentAddress,
                currentCity,
                currentState,
                currentZip,
                phone,
                dob,
                ssn,
              } = form.getState().values;
              setPidAnswers((state) => ({
                ...state,
                firstName: firstName ?? '',
                middleName: middleName ?? '',
                lastName: lastName ?? '',
                currentAddress: currentAddress ?? '',
                currentCity: currentCity ?? '',
                currentState: currentState ?? '',
                currentZip: currentZip ?? '',
                phone: phone ?? '',
                dob: dob ?? '',
                ssn: ssn ?? '',
              }));

              if (skipTermsStep) {
                abortController.abort();
                onRequestStep(IdvStep.LandingStep);
              }
            }}
            onSubmit={getWizardOnSubmit(form)}
            render={({ handleSubmit }: { handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void }) => (
              <PersonalInfoStepContent handleSubmit={handleSubmit} />
            )}
          />
        );
      }}
    />
  );
}
